import React, { memo } from "react";
import Select from "react-select";

const CustomSelect = ({
  options = [],
  value = null,
  onChange,
  placeholder = "Select...",
  isClearable = false,
  isDisabled = false,
  isMulti = false,
  styles = {},
  className = "",
  ...props
}) => {
  const menuPortalTarget =
    typeof document !== "undefined" ? document.body : null;

  const getCssVar = (name, fallback) => {
    if (typeof window === "undefined") return fallback;

    const value = getComputedStyle(document.documentElement)
      .getPropertyValue(name)
      .trim();

    return value || fallback;
  };

  const themeColors = {
    inputBg: getCssVar("--input_bg_color"),
    inputBorder: getCssVar("--input_border_color"),
    text: getCssVar("--font_default_color"),
    muted: getCssVar("--gary-color"),
    primary: getCssVar("--main-color"),
    white: getCssVar("--white-color"),
    hover: getCssVar("--table_tr_hover"),
    cardBg: getCssVar("--cardBg_color"),
  };

  const defaultStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: "2.2rem",
      height: "2.2rem",
      backgroundColor: state.isDisabled ? "#b1afaf70" : themeColors.inputBg,
      borderColor: themeColors.inputBorder,
      boxShadow: "none",
      color: themeColors.text,
      "&:hover": {
        borderColor: themeColors.inputBorder,
      },
    }),
    option: (base, state) => ({
      ...base,
      padding: "8px 10px",
      fontSize: "13px",
      fontWeight: 500,
      color: state.isSelected ? themeColors.white : themeColors.text,
      backgroundColor: state.isSelected
        ? themeColors.primary
        : state.isFocused
          ? themeColors.hover
          : "transparent",
      cursor: "pointer",
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: themeColors.inputBg,
      border: `1px solid ${themeColors.inputBorder}`,
      boxShadow: "0 10px 24px rgba(15, 23, 42, 0.12)",
      overflow: "hidden",
      zIndex: 11,
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    menuList: (base) => ({
      ...base,
      padding: 0,
    }),
    singleValue: (base) => ({
      ...base,
      color: themeColors.text,
      fontSize: "13px",
    }),
    placeholder: (base) => ({
      ...base,
      color: themeColors.text,
      fontSize: "14px",
      lineHeight: 1.1,
    }),
    input: (base) => ({
      ...base,
      color: themeColors.text,
      fontSize: "13px",
    }),
    valueContainer: (base) => ({
      ...base,
      minHeight: "2.2rem",
      padding: "1px 8px 2px",
      overflow: "hidden",
    }),
    multiValue: (base) => ({
      ...base,
      background: themeColors.primary,
      margin: "1px 0 4px",
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: themeColors.white,
      fontSize: "13px",
    }),
    indicatorSeparator: (base) => ({
      ...base,
      backgroundColor: themeColors.inputBorder,
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: themeColors.muted,
    }),
    clearIndicator: (base) => ({
      ...base,
      color: themeColors.muted,
    }),
  };

  return (
    <Select
      options={options}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isMulti={isMulti}
      classNamePrefix="custom-select"
      className={className}
      // menuIsOpen={true}
      menuPortalTarget={menuPortalTarget}
      menuPosition="fixed"
      theme={(theme) => ({
        ...theme,
        borderRadius: 6,
        colors: {
          ...theme.colors,
          primary: themeColors.primary,
          primary25: themeColors.hover,
          primary50: themeColors.hover,
          neutral0: themeColors.cardBg,
          neutral20: themeColors.inputBorder,
          neutral30: themeColors.inputBorder,
          neutral40: themeColors.muted,
          neutral50: themeColors.text,
          neutral60: themeColors.muted,
          neutral80: themeColors.text,
        },
      })}
      styles={{ ...defaultStyles, ...styles }}
      {...props}
    />
  );
};

export default memo(CustomSelect);
